import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Card,
  Typography,
  IconButton,
  Grid,
  Paper,
  Button,
  LinearProgress,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
} from "@mui/material";
import {
  FullscreenOutlined,
  FullscreenExitOutlined,
  RefreshOutlined,
} from "@mui/icons-material";
import MapIcon from "@mui/icons-material/Map";
import GetAppIcon from "@mui/icons-material/GetApp";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DescriptionIcon from "@mui/icons-material/Description";
import TableViewIcon from "@mui/icons-material/TableView";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import * as XLSX from "xlsx";
//for pdf Export
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "../../utils/axiosInstance";
import UnitsTableOnMapView from "./UnitsTableOnMapView";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ReorderIcon from "@mui/icons-material/Reorder";
import WifiIcon from "@mui/icons-material/Wifi";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import DoNotDisturbAltIcon from "@mui/icons-material/DoNotDisturbAlt";
import LocationOnIcon from "@mui/icons-material/LocationOn";

const ViewOnMap = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const loading = loadingStats || loadingTable;
  const [filteredUnits, setFilteredUnits] = useState([]);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const markerClusterGroupRef = useRef(null);
  const leafletRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("All Units");
  const { type, id, name } = location.state || {};
  const [percentages, setPercentages] = useState({});
  const [totals, setTotals] = useState({});
  const [tableData, setTableData] = useState([]);
  const [isClusterEnabled, setIsClusterEnabled] = useState(true);
  const individualMarkersRef = useRef([]);
  const [sortConfig, setSortConfig] = useState({});
  const headerKeyMap = {
  "Unit Name": "unitName",
  "Region/Technician": "regionTechnician",
  "Shutdown Enum": "shutdownEnum",
  "Sample Time": "sampleTimeRaw",
  "Modem Serial Number": "modemSerialNumber",
  "Latitude": "latitude",
  "Longitude": "longitude",
};

  const isOlderThan24Hours = (sampleTime) => {
    if (!sampleTime) return true; // If no time provided, consider it as not working

    try {
      const sampleDate = new Date(sampleTime);
      const currentDate = new Date();
      const diffInHours = (currentDate - sampleDate) / (1000 * 60 * 60);
      return diffInHours > 24;
    } catch (error) {
      console.error("Error parsing date:", error);
      return true; // If error in parsing, consider it as not working
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return " ";
    const date = new Date(timeString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const gotoUnitPage = (id) => {
    navigate(`../device/${id}/engine-data`);
  };

  const calcStats = (units) => {
    const total = units.length;
    if (total === 0) {
      return { total: 0, online: 0, offline: 0, alarm: 0 };
    }

    let online = 0;
    let offline = 0;
    let alarm = 0;

    units.forEach((u) => {
      if (u.time !== "N/A" && u.stateDesc !== "SHUTDOWN" && !isOlderThan24Hours(u.time)) {
        online++;
      } else if (u.stateDesc === "SHUTDOWN" && !isOlderThan24Hours(u.time)) {
        alarm++;
      } else if (u.time !== "N/A" && isOlderThan24Hours(u.time)) {
        offline++;
      }
    });
    console.log(alarm)

    // offline = total - online;

    const percentages = {
      total,
      online: total ? Math.round((online / total) * 100) : 0,
      offline: total ? Math.round((offline / total) * 100) : 0,
      alarm: total ? Math.round((alarm / total) * 100) : 0,
    };

    const totals = {
      total,
      offline,
      alarm,
    };
    return { percentages, totals };
  };

  const fetchTableAreaData = async () => {
    try {
      setLoadingTable(true);
      const params = {
        companyId: process.env.REACT_APP_COMPANY_ID,
      };
      if (type === "group") {
        params.groupId = id;
      } else if (type === "region") {
        params.regionId = id;
      } else if (type === "route") {
        params.routeId = id;
      }
      const res = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/specific-area-status`,
        { params }
      );
      if (res.data?.data) {
        setTableData(res.data.data);
        const { percentages, totals } = calcStats(res.data.data);
        setPercentages(percentages);
        setTotals(totals);
      } else {
        console.error("Invalid API structure:", res.data);
      }
    } catch (error) {
      console.error("Failed to fetch unit table data:", error);
    } finally {
      setLoadingTable(false);
    }
  };

  const tabConfig = {
    "All Units": {
      icon: <ReorderIcon />,
      headers: [
        "Unit Name",
        "Region/Technician",
        "Shutdown Enum",
        "Sample Time",
        "Unit Page",
      ],
      columns: [
      "unitName",
      "regionTechnician",
      "shutdownEnum",
      "sampleTime",
      "data",
    ],
      rows: tableData.map((u) => ({
        unitName: u.name || " ",
        regionTechnician: u.routeId
          ? `${name} - ${u.routeName}`
          : `${name} - ${u.regionName}`,
        shutdownEnum: u.SDcodeDesc ? u.SDcodeDesc : "-",
        // raw value for sorting
        sampleTimeRaw: u.time ? new Date(u.time).getTime() : null,
        sampleTime: u.time ? formatTime(u.time) : " ",
        data: (
          <Button
            variant="contained"
            size="small"
            onClick={() => gotoUnitPage(u.id)}
          >
            View
          </Button>
        ),
      })),
    },
    "Online Units": {
      icon: <WifiIcon />,
      headers: [
        "Unit Name",
        "Region/Technician",
        "Shutdown Enum",
        "Sample Time",
        "Unit Page",
      ],
       columns: [
      "unitName",
      "regionTechnician",
      "shutdownEnum",
      "sampleTime",
      "data",
    ],
      rows: tableData
        .filter((u) => u.time !== "N/A" && u.stateDesc !== "SHUTDOWN" && !isOlderThan24Hours(u.time))
        .map((u) => ({
          unitName: u.name || " ",
          regionTechnician: u.routeId
            ? `${name} - ${u.routeName}`
            : `${name} - ${u.regionName}`,
          shutdownEnum: u.SDcodeDesc ? u.SDcodeDesc : " ",
          // raw value for sorting
        sampleTimeRaw: u.time ? new Date(u.time).getTime() : null,
          sampleTime: u.time ? formatTime(u.time) : " ",
          data: (
            <Button
              variant="contained"
              size="small"
              onClick={() => gotoUnitPage(u.id)}
            >
              View
            </Button>
          ),
        })),
    },
    "Offline Units": {
      icon: <CloseIcon />,
      headers: [
        "Unit Name",
        "Region/Technician",
        "Modem Serial Number",
        "Shutdown Enum",
        "Sample Time",
        "Unit Page",
      ],
       columns: [
      "unitName",
      "regionTechnician",
      "modemSerialNumber",
      "shutdownEnum",
      "sampleTime",
      "data",
    ],
      rows: tableData
        .filter((u) => u.time !== "N/A" && isOlderThan24Hours(u.time))
        .map((u) => ({
          unitName: u.name || " ",
          regionTechnician: u.routeId
            ? `${name} - ${u.routeName}`
            : `${name} - ${u.regionName}`,
          modemSerialNumber: u.id ? u.id : " ",
          shutdownEnum: u.SDcodeDesc ? u.SDcodeDesc : " ",
          // raw value for sorting
        sampleTimeRaw: u.time ? new Date(u.time).getTime() : null,
          sampleTime: u.time ? formatTime(u.time) : " ",
          data: (
            <Button
              variant="contained"
              size="small"
              onClick={() => gotoUnitPage(u.id)}
            >
              View
            </Button>
          ),
        })),
    },
    "Alarms Units": {
      icon: <WarningIcon />,
      headers: [
        "Unit Name",
        "Region/Technician",
        "Shutdown Enum",
        "Sample Time",
        "Unit Page",
      ],
      columns: [
      "unitName",
      "regionTechnician",
      "shutdownEnum",
      "sampleTime",
      "data",
    ],
      rows: tableData
        .filter((u) => u.stateDesc === "SHUTDOWN" && !isOlderThan24Hours(u.time))
        .map((u) => ({
          unitName: u.name || " ",
          regionTechnician: u.routeId
            ? `${name} - ${u.routeName}`
            : `${name} - ${u.regionName}`,
          shutdownEnum: u.SDcodeDesc ? u.SDcodeDesc : " ",
          // raw value for sorting
        sampleTimeRaw: u.time ? new Date(u.time).getTime() : null,
          sampleTime: u.time ? formatTime(u.time) : " ",
          data: (
            <Button
              variant="contained"
              size="small"
              onClick={() => gotoUnitPage(u.id)}
            >
              View
            </Button>
          ),
        })),
    },
    "No Contact": {
      icon: <DoNotDisturbAltIcon />,
      headers: [
        "Unit Name",
        "Region/Technician",
        "Modem Serial Number",
        "Sample Time",
        "Unit Page",
      ],
      columns: [
      "unitName",
      "regionTechnician",
      "shutdownEnum",
      "sampleTime",
      "data",
    ],
      rows: tableData
        .filter((u) => u.time === "N/A")
        .map((u) => ({
          unitName: u.name || " ",
          regionTechnician: u.routeId
            ? `${name} - ${u.routeName}`
            : `${name} - ${u.regionName}`,
          modemSerialNumber: u.id ? u.id : " ",
          // raw value for sorting
        sampleTimeRaw: u.time ? new Date(u.time).getTime() : null,
          sampleTime: u.time ? "No contact" : " ",
          data: (
            <Button
              variant="contained"
              size="small"
              onClick={() => gotoUnitPage(u.id)}
            >
              View
            </Button>
          ),
        })),
    },
    GPS: {
      icon: <LocationOnIcon />,
      headers: ["Unit Name", "Latitude", "Longitude"],
      rows: filteredUnits.map((u) => ({
        unitName: u.name || " ",
        latitude: u.latitude ? parseFloat(u.latitude).toFixed(6) : " ",
        longitude: u.longitude ? parseFloat(u.longitude).toFixed(6) : " ",
      })),
    },
  };

   const handleSort = (tabKey, column) => {
    setSortConfig((prev) => {
      const current = prev[tabKey];
      if (current?.column === column) {
        return {
          ...prev,
          [tabKey]: {
            column,
            direction: current.direction === "asc" ? "desc" : "asc",
          },
        };
      }

      return {
        ...prev,
        [tabKey]: { column, direction: "asc" },
      };
    });
};

const getSortedRows = (tabKey) => {
  const { rows } = tabConfig[tabKey];
  const config = sortConfig[tabKey];
  if (!config) return rows;

  const { column, direction } = config;

  return [...rows].sort((a, b) => {
    const aVal = a[column];
    const bVal = b[column];

    if (aVal == null) return 1;
    if (bVal == null) return -1;

    // timestamps / numbers
  if (typeof aVal === "number" && typeof bVal === "number") {
    return direction === "asc" ? aVal - bVal : bVal - aVal;
  }

    // strings
  const result = aVal.toString().localeCompare(bVal.toString());
  return direction === "asc" ? result : -result;
  });
};

  const currentTabData = tabConfig[activeTab];
  const tabRowCount = currentTabData?.rows?.length ?? 0;

  const sortedRows = getSortedRows(activeTab);

  const filteredTableRows = sortedRows.filter((row) =>
    Object.values(row).some((value) =>
      value?.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullScreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
  }, []);

  const handleFullScreenToggle = () => {
    const mapElement = mapRef.current;
    if (!mapElement) return;

    if (!document.fullscreenElement) {
      mapElement.requestFullscreen().catch((err) => {
        console.error(`Error enabling fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const colors = [
    "#2196f3", // blue
  ];

  // fetch the % & total data
  useEffect(() => {
    fetchTableAreaData();
  }, [type, id]);

  const circularItems = [
    { label: "Online Units (%)", color: "#4caf50", value: percentages?.online },
    {
      label: "Offline Units (%)",
      color: "#ff9800",
      value: percentages?.offline,
    },
    { label: "Alarm Units (%)", color: "#f92718ff", value: percentages?.alarm },
  ];

  const barItems = [
    {
      label: "Total Units",
      color: "#00aad1ff",
      value: 100,
      count: totals?.total,
    },
    {
      label: "Offline",
      color: "#ff9800",
      value: 100,
      count: totals?.offline,
    },
    {
      label: "Alarm Units",
      color: "#f44336",
      value: 100,
      count: totals?.alarm,
    },
  ];
  const token = localStorage.getItem("token")
  // Fetch units from API
  useEffect(() => {
    const fetchUnitsOnMap = async () => {
      try {
        setLoadingStats(true);
        const params = {
          companyId: process.env.REACT_APP_COMPANY_ID,
        };
        if (type === "group") {
          params.groupId = id;
        } else if (type === "region") {
          params.regionId = id;
        } else if (type === "route") {
          params.routeId = id;
        }
        let Api_return = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/iot/speceficdeviceslatlon`,
          {
            params,
            headers: {
              Authorization: `Bearer ${localStorage.getItem(token)}`, // <-- add token here
            },
          }
        );
        let data = Api_return.data;
        const mappedData = data.map((unit) => {
          return {
            id: unit.id,
            name: unit.name,
            latitude: unit.latitude,
            longitude: unit.longitude,
            macaddr: unit.macaddr,
            groupId: unit.groupId,
          };
        });
        setFilteredUnits(mappedData);
      } catch (err) {
        console.error("Error fetching devices:", err);
        setFilteredUnits([]);
      } finally {
        setLoadingStats(false);
      }
    };
    fetchUnitsOnMap();
  }, [type, id]);

  // Load Leaflet and MarkerCluster plugin
  useEffect(() => {
    let isCancelled = false;

    const loadLeaflet = async () => {
      try {
        // Load Leaflet CSS
        if (!document.querySelector('link[href*="leaflet.css"]')) {
          const leafletCSS = document.createElement("link");
          leafletCSS.rel = "stylesheet";
          leafletCSS.href =
            "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.css";
          document.head.appendChild(leafletCSS);
        }

        // Load MarkerCluster CSS
        if (!document.querySelector('link[href*="MarkerCluster"]')) {
          const clusterCSS = document.createElement("link");
          clusterCSS.rel = "stylesheet";
          clusterCSS.href =
            "https://cdnjs.cloudflare.com/ajax/libs/leaflet.markercluster/1.5.3/MarkerCluster.css";
          document.head.appendChild(clusterCSS);

          const clusterDefaultCSS = document.createElement("link");
          clusterDefaultCSS.rel = "stylesheet";
          clusterDefaultCSS.href =
            "https://cdnjs.cloudflare.com/ajax/libs/leaflet.markercluster/1.5.3/MarkerCluster.Default.css";
          document.head.appendChild(clusterDefaultCSS);
        }

        // Load Leaflet JS
        if (!window.L) {
          await new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src =
              "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        // Load MarkerCluster plugin
        if (!window.L.markerClusterGroup) {
          await new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src =
              "https://cdnjs.cloudflare.com/ajax/libs/leaflet.markercluster/1.5.3/leaflet.markercluster.min.js";
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        if (!isCancelled) {
          leafletRef.current = window.L;
          initializeMap();
        }
      } catch (error) {
        console.error("Error loading Leaflet:", error);
      }
    };

    if (mapRef.current && !leafletMapRef.current) {
      loadLeaflet();
    }

    return () => {
      isCancelled = true;
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
    };
  }, []);

  // Update markers when units data changes OR cluster mode changes
  useEffect(() => {
    if (isMapLoaded && filteredUnits.length > 0) {
      addMarkersToMap();
    }
  }, [filteredUnits, isMapLoaded, isClusterEnabled]);

  const initializeMap = () => {
    if (!leafletRef.current || !mapRef.current || leafletMapRef.current) return;

    const L = leafletRef.current;

    // Fix default markers issue
    if (L.Icon.Default.prototype._getIconUrl) {
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl:
          "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
        iconUrl:
          "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
        shadowUrl:
          "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
      });
    }

    leafletMapRef.current = L.map(mapRef.current, {
      center: [39.8283, -98.5795],
      zoom: 5,
      zoomControl: true,
      minZoom: 2,
      maxZoom: 18,
      maxBounds: [
        [-90, -180],
        [90, 180],
      ],
      maxBoundsViscosity: 1.0,
    });

    // Satellite/Earth view tile layer
    const satelliteLayer = L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      {
        attribution: "",
        maxZoom: 19,
      }
    );

    // Second layer: Labels overlay
    const labelsLayer = L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
      {
        attribution: "",
        maxZoom: 19,
        opacity: 0.8,
      }
    );

    // Add both layers to the map
    satelliteLayer.addTo(leafletMapRef.current);
    labelsLayer.addTo(leafletMapRef.current);

    // Initialize MarkerClusterGroup with custom styling
    markerClusterGroupRef.current = L.markerClusterGroup({
      maxClusterRadius: 150,
      iconCreateFunction: function (cluster) {
        const count = cluster.getChildCount();
        let size = "small";
        let className = "marker-cluster-small";

        if (count < 30) {
          size = "small";
          className = "marker-cluster-small";
        } else if (count < 80) {
          size = "medium";
          className = "marker-cluster-medium";
        } else {
          size = "large";
          className = "marker-cluster-large";
        }

        return new L.DivIcon({
          html: `<div><span>${count}</span></div>`,
          className: `marker-cluster ${className}`,
          iconSize: new L.Point(40, 40),
        });
      },
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
    });

    leafletMapRef.current.addLayer(markerClusterGroupRef.current);

    // Add custom CSS for clusters
    if (!document.querySelector("#cluster-styles")) {
      const style = document.createElement("style");
      style.id = "cluster-styles";
      style.textContent = `
                .marker-cluster-small {
                    background-color: rgba(181, 226, 140, 0.6);
                    border: 2px solid rgba(110, 204, 57, 1);
                }
                .marker-cluster-small div {
                    background-color: rgba(110, 204, 57, 0.8);
                }
                .marker-cluster-medium {
                    background-color: rgba(241, 211, 87, 0.6);
                    border: 2px solid rgba(240, 194, 12, 1);
                }
                .marker-cluster-medium div {
                    background-color: rgba(240, 194, 12, 0.8);
                }
                .marker-cluster-large {
                    background-color: rgba(253, 156, 115, 0.6);
                    border: 2px solid rgba(241, 128, 23, 1);
                }
                .marker-cluster-large div {
                    background-color: rgba(241, 128, 23, 0.8);
                }
                .marker-cluster {
                    border-radius: 50%;
                    text-align: center;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                    font-weight: bold;
                }
                .marker-cluster div {
                    width: 30px;
                    height: 30px;
                    margin-left: 5px;
                    margin-top: 5px;
                    text-align: center;
                    border-radius: 50%;
                    font: 12px "Helvetica Neue", Arial, Helvetica, sans-serif;
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .marker-cluster span {
                    line-height: 1;
                }
            `;
      document.head.appendChild(style);
    }

    setIsMapLoaded(true);
  };

  const addMarkersToMap = () => {
    if (
      !leafletMapRef.current ||
      !leafletRef.current ||
      !markerClusterGroupRef.current
    )
      return;

    const L = leafletRef.current;

    // Clear existing markers from both cluster and individual arrays
    markerClusterGroupRef.current.clearLayers();
    individualMarkersRef.current.forEach((marker) =>
      leafletMapRef.current.removeLayer(marker)
    );
    individualMarkersRef.current = [];

    filteredUnits.forEach((unit, index) => {
      const lat =
        unit.latitude && unit.latitude !== ""
          ? parseFloat(unit.latitude)
          : null;
      const lng =
        unit.longitude && unit.longitude !== ""
          ? parseFloat(unit.longitude)
          : null;

      if (lat === null || lng === null) return;

      const icon = L.divIcon({
        className: "custom-div-icon",
        html: `<div style="background-color: ${
          colors[index % colors.length]
        }; width: 20px; height: 20px; border-radius: 50%; border: 2px solid white; box-shadow: 0 1px 3px rgba(0,0,0,0.4);"></div>`,
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      });

      const marker = L.marker([lat, lng], { icon }).bindPopup(
        `<strong>${unit.name || unit.id}</strong><br/>ID: ${
          unit.id
        }<br/>Lat: ${lat.toFixed(6)}<br/>Lng: ${lng.toFixed(6)}`
      );

      if (isClusterEnabled) {
        // Add to cluster group
        markerClusterGroupRef.current.addLayer(marker);
      } else {
        // Add directly to map as individual marker
        marker.addTo(leafletMapRef.current);
        individualMarkersRef.current.push(marker);
      }
    });

    // Optional: zoom map to fit filtered markers
    if (filteredUnits.length > 0) {
      const group = isClusterEnabled
        ? new L.featureGroup(markerClusterGroupRef.current.getLayers())
        : new L.featureGroup(individualMarkersRef.current);

      if (group.getLayers().length > 0) {
        leafletMapRef.current.fitBounds(group.getBounds().pad(0.2));
      }
    }
  };

  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const copyToClipboard = () => {
    const headers = currentTabData.headers.filter(
      (h) => h !== "Unit Page"
    );
    const rows = currentTabData.rows;
    if (!rows || rows.length === 0) {
      console.warn("No data to copy");
      toast.error("No data to copy");
      return;
    }
    // Create tab-separated text
    const textContent = [
      headers.join("\t"),
      ...rows.map((row) =>
        headers
          .map(
            (header) =>
              row[
                header
                  .replace(/\s*\/\s*/g, "") // Remove slashes cleanly
                  .replace(/\s+/g, " ") // Normalize spaces
                  .replace(/\b(\w)/g, (m, c, i) =>
                    i === 0 ? c.toLowerCase() : c
                  ) // lowercase first letter only
                  .replace(/\s+(\w)/g, (_, c) => c.toUpperCase()) // turn spaces into camelCase
                  .replace(/\s+/g, "")
              ] ?? ""
          )
          .join("\t")
      ),
    ].join("\n");

    navigator.clipboard
      .writeText(textContent)
      .then(() => toast.success("Data copied to clipboard"))
      .catch((err) => toast.error("Failed to copy:", err));

    handleExportMenuClose();
  };

  const exportToCSV = () => {
    const headers = currentTabData.headers.filter(
      (h) => h !== "Unit Page"
    );
    const rows = currentTabData.rows;

    if (!rows || rows.length === 0) {
      console.warn("No data to export");
      toast.error("No data to export");
      return;
    }
    const csvContent = [
      headers,
      ...rows.map((row) =>
        headers.map(
          (header) =>
            row[
              header
                .replace(/\s*\/\s*/g, "") // Remove slashes cleanly
                .replace(/\s+/g, " ") // Normalize spaces
                .replace(/\b(\w)/g, (m, c, i) =>
                  i === 0 ? c.toLowerCase() : c
                ) // lowercase first letter only
                .replace(/\s+(\w)/g, (_, c) => c.toUpperCase()) // turn spaces into camelCase
                .replace(/\s+/g, "")
            ] ?? ""
        )
      ),
    ];

    const csvString = csvContent
      .map((r) => r.map((f) => `"${f}"`).join(","))
      .join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${activeTab.replace(/\s+/g, "_")}_Data.csv`;
    link.click();

    handleExportMenuClose();
    toast.success("Exported to CSV");
  };

  const exportToExcel = () => {
    const headers = currentTabData.headers.filter(
      (h) => h !== "Unit Page"
    );
    const rows = currentTabData.rows;

    if (!rows || rows.length === 0) {
      console.warn("No data to export");
      toast.error("No data to export");
      return;
    }

    const worksheetData = [
      headers,
      ...rows.map((row) =>
        headers.map(
          (header) =>
            row[
              header
                .replace(/\s*\/\s*/g, "") // Remove slashes cleanly
                .replace(/\s+/g, " ") // Normalize spaces
                .replace(/\b(\w)/g, (m, c, i) =>
                  i === 0 ? c.toLowerCase() : c
                ) // lowercase first letter only
                .replace(/\s+(\w)/g, (_, c) => c.toUpperCase()) // turn spaces into camelCase
                .replace(/\s+/g, "")
            ] ?? ""
        )
      ),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, `${activeTab.replace(/\s+/g, "_")}_Data.xlsx`);

    handleExportMenuClose();
    toast.success("Exported to Excel");
  };

  const exportToPDF = () => {
    const headers = currentTabData.headers.filter(
      (h) => h !== "Unit Page"
    );
    const rows = currentTabData.rows;

    if (!rows || rows.length === 0) {
      console.warn("No data to export");
      toast.error("No data to export");
      return;
    }

    const doc = new jsPDF("l", "pt", "a4");

    doc.setFontSize(12);
    doc.text(`${activeTab} Data`, 40, 30);

    const tableBody = rows.map((row) =>
      headers.map(
        (header) =>
          row[
            header
              .replace(/\s*\/\s*/g, "") // Remove slashes cleanly
              .replace(/\s+/g, " ") // Normalize spaces
              .replace(/\b(\w)/g, (m, c, i) => (i === 0 ? c.toLowerCase() : c)) // lowercase first letter only
              .replace(/\s+(\w)/g, (_, c) => c.toUpperCase()) // turn spaces into camelCase
              .replace(/\s+/g, "")
          ] ?? ""
      )
    );

    autoTable(doc, {
      head: [headers],
      body: tableBody,
      startY: 50,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 3,
        overflow: "linebreak",
      },
      headStyles: { fillColor: [41, 128, 185] },
    });

    doc.save(`${activeTab.replace(/\s+/g, "_")}_Data.pdf`);
    handleExportMenuClose();
    toast.success("Exported to PDF");
  };

  return (
  <Box
  sx={{
    display: "flex",
    flexDirection: { xs: "column", md: "column", lg:"row" },
    gap: 2,
    width: "100%",
    minHeight: "90vh",
    p: 2,
  }}
>
  {/* ================= LEFT SIDE ================= */}
  <Box
    sx={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      gap: 2,
      minHeight: 0,
    }}
  >
    {/* -------- AREA STATUS -------- */}
    <Card sx={{ p: 2 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography sx={{ fontSize: 20 }}>{name} Area Status</Typography>
        <IconButton
          onClick={fetchTableAreaData}
          disabled={loading}
          sx={{
            color: loading ? "grey.500" : "#00aad1ff",
            transition: "transform 0.3s",
            "&:hover": { transform: "rotate(90deg)" },
          }}
        >
          <RefreshOutlined />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Grid container spacing={3} justifyContent="center">
          {circularItems.map((item) => (
            <Grid xs={6} sm={4} md={2} key={item.label}>
              <Paper sx={{ textAlign: "center", p: 1, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {item.label}
                </Typography>

                <Box sx={{ position: "relative", display: "inline-flex" }}>
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={80}
                    thickness={5}
                    sx={{ color: "#ccc", opacity: 0.3, position: "absolute" }}
                  />
                  <CircularProgress
                    variant="determinate"
                    value={loading ? 0 : item.value}
                    size={80}
                    thickness={5}
                    sx={{ color: item.color }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: 16 }}>
                      {loading ? "..." : `${item.value}%`}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: { xs: "100%", md: 260 },
          }}
        >
          {barItems.map((bar) => (
            <Box key={bar.label}>
              <Typography variant="body2">
                {bar.label}: {loading ? "..." : bar.count}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={bar.value}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  "& .MuiLinearProgress-bar": { bgcolor: bar.color },
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Card>

    {/* -------- MAP -------- */}
    <Card
      sx={{
        p: 2,
        flex: 1,
        minHeight: { xs: 280, sm: 350, md: 420 },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography sx={{ fontSize: 20 }}>{name} Area Map</Typography>
        <IconButton onClick={handleFullScreenToggle}>
          {isFullScreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
        </IconButton>
      </Box>

      <Box display="flex" justifyContent="flex-end" gap={1} mb={1}>
        <Button
          size="small"
          variant={isClusterEnabled ? "contained" : "outlined"}
          onClick={() => setIsClusterEnabled(true)}
          startIcon={<MapIcon />}
        >
          Group Map
        </Button>
        <Button
          size="small"
          variant={!isClusterEnabled ? "contained" : "outlined"}
          onClick={() => setIsClusterEnabled(false)}
          startIcon={<LocationOnIcon />}
        >
          Marker Map
        </Button>
      </Box>

      <Box
        ref={mapRef}
        sx={{
          flex: 1,
          width: "100%",
          minHeight: 0,
          borderRadius: 2,
          position: "relative",
        }}
      >
        {!isMapLoaded && (
          <Typography
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Loading map...
          </Typography>
        )}
      </Box>
    </Card>
  </Box>

  {/* ================= RIGHT SIDE ================= */}
  <Box
    sx={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
    }}
  >
    <Card
      sx={{
        p: 2,
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Typography sx={{ fontSize: 20 }} mb={1}>
        Details
      </Typography>

      <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
        {Object.entries(tabConfig).map(([label, config]) => (
          <Button
            key={label}
            size="small"
            variant={activeTab === label ? "contained" : "outlined"}
            onClick={() => setActiveTab(label)}
            startIcon={config.icon}
          >
            {label}
          </Button>
        ))}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="outlined"
          startIcon={<GetAppIcon />}
          onClick={handleExportMenuOpen}
        >
          Export
        </Button>

        <TextField
          size="small"
          label="Search Unit..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </Box>

      {/* TABLE SCROLL AREA */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          minHeight: 0,
          maxHeight: {
            xs: "45vh",
            sm: "50vh",
            md: "60vh",
            lg: "70vh",
          },
        }}
      >
        <UnitsTableOnMapView
          headers={currentTabData.headers}
          columns={currentTabData.columns}
          rows={filteredTableRows}
          onSort={(column) => handleSort(activeTab, column)}
          sortConfig={sortConfig[activeTab]}
          headerKeyMap={headerKeyMap}
          loading={loading}
        />
      </Box>
      <Box
  sx={{
    display: "flex",
    justifyContent: "flex-end",
    mt: 1.5,
    px: 1,
  }}
>
  <Typography variant="body2" color="text.secondary">
    Total: {tabRowCount} unit{tabRowCount !== 1 ? "s" : ""}
  </Typography>
</Box>
    </Card>
  </Box>

  {/* EXPORT MENU */}
  <Menu
    anchorEl={exportMenuAnchor}
    open={Boolean(exportMenuAnchor)}
    onClose={handleExportMenuClose}
  >
    <MenuItem onClick={copyToClipboard}>
      <ListItemIcon>
        <FileCopyIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>Copy</ListItemText>
    </MenuItem>
    <MenuItem onClick={exportToCSV}>
      <ListItemIcon>
        <DescriptionIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>CSV</ListItemText>
    </MenuItem>
    <MenuItem onClick={exportToExcel}>
      <ListItemIcon>
        <TableViewIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>Excel</ListItemText>
    </MenuItem>
    <MenuItem onClick={exportToPDF}>
      <ListItemIcon>
        <PictureAsPdfIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>PDF</ListItemText>
    </MenuItem>
  </Menu>
</Box>

  );
};

export default ViewOnMap;
